// ts-rest contract for the Desk per-page typed endpoints
// (client-knowledge, calls-meetings). Replaces useTable + named-action
// dispatches for the Desk surface.
//
// Per ADR-0018 the response rows the server knows the shape of are real
// `z.object` schemas, not `z.record(z.unknown())` stand-ins. The genuinely-
// loose populations that survive are named at their use sites below.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionErrorSchema } from './agent';
import { clientStubSchema } from '../schemas/clients';

const c = initContract();

const errorBody = z.object({ error: z.string() });

// --- Client Knowledge KB (per-client "what we know") ------------------------
// The Client Knowledge page reads its picker from the canonical `Client`
// ontology object (one entry per firm Client), and a per-client knowledge base
// composed from the REAL ATLAS call-extracted artefacts — `call_kb_notes`
// (markdown KB notes) and `call_extracted_facts` (categorised facts). Each KB
// is keyed by the `Client.id`, so `deskKnowledgeBases[clientId]` == the picker
// id == the artefacts' `clientId`. Presentation projection (ADR-0008) of the
// CallKbNote / CallExtractedFact artefacts; the page renders these shapes
// directly (mirrors apps/web/src/data/types.ts `DeskKBNote` / `DeskKBGraphNode`
// / `DeskClientKB`).

/** A KB note as the Client Knowledge page renders it (a `CallKbNote` row, or a
 *  single `CallExtractedFact` projected as a note-like entry). */
export const deskKBNoteSchema = z.object({
    id: z.string(),
    title: z.string(),
    content: z.string(),
    template: z.string(),
    tags: z.array(z.string()),
    dateModifiedLabel: z.string(),
    linkedNotes: z.array(z.string()),
    emailProvenance: z.string().nullable(),
    agentGenerated: z.boolean().optional(),
    // The call this note was drafted from, when it is an ATLAS artefact —
    // resolves the `[^N]` citations in `content` against the call transcript.
    callId: z.string().nullable().optional(),
});
export type DeskKBNote = z.infer<typeof deskKBNoteSchema>;

/** A relationship-graph node — one `CallExtractedFact` category for a client,
 *  with `connectionCount` = number of facts in that category. */
export const deskKBGraphNodeSchema = z.object({
    id: z.string(),
    label: z.string(),
    category: z.string(),
    connectionCount: z.number(),
    x: z.number(),
    y: z.number(),
});
export type DeskKBGraphNode = z.infer<typeof deskKBGraphNodeSchema>;

export const deskClientKBSchema = z.object({
    id: z.string(),
    clientName: z.string(),
    notes: z.array(deskKBNoteSchema),
    graphNodes: z.array(deskKBGraphNodeSchema),
    lastUpdatedLabel: z.string(),
});
export type DeskClientKB = z.infer<typeof deskClientKBSchema>;

/** A Markdown document an IM attached by hand to a client's knowledge base
 *  (a `ClientKnowledgeDocument` row). Distinct from the ATLAS artefacts: it is
 *  human-authored Markdown and is removable by its IM. */
export const clientKnowledgeDocumentSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    title: z.string(),
    markdownContent: z.string(),
    createdByUserId: z.string(),
    createdAt: z.string(),
    createdAtLabel: z.string(),
    sourceFilename: z.string().nullable(),
});
export type ClientKnowledgeDocumentDto = z.infer<typeof clientKnowledgeDocumentSchema>;

export const deskClientKnowledgeResponseSchema = z.object({
    clients: z.array(clientStubSchema),
    // Per-client knowledge bases, keyed by `Client.id`. A client with no ATLAS
    // artefacts yet is simply absent from the map (the page treats a missing
    // entry as an empty KB).
    deskKnowledgeBases: z.record(deskClientKBSchema),
    // Per-client IM-authored Markdown documents (ClientKnowledgeDocument rows),
    // keyed by `Client.id`. Soft-deleted documents are excluded. A client with
    // no manual documents is simply absent from the map.
    clientKnowledgeDocuments: z.record(z.array(clientKnowledgeDocumentSchema)),
});
export type DeskClientKnowledgeResponse = z.infer<typeof deskClientKnowledgeResponseSchema>;

// --- Calls & Meetings -------------------------------------------------------
// The composed payload draws from four firm-infrastructure tables. Each row is
// projected to its honest DTO (camelCase wire keys mirror the Drizzle columns).

/** A logged call (the `calls` table — minimal: id / client / status). */
export const deskCallRowSchema = z.object({
    id: z.string(),
    clientId: z.string().nullable(),
    status: z.string().nullable(),
    // Display fields projected from the desk_calls row (Numonix → ElevenLabs →
    // DeskCall). Nullable/defaulted so a freshly-ingested call (before ATLAS
    // scores sentiment/summary) still renders cleanly.
    clientName: z.string().nullable(),
    callType: z.string().nullable(),
    callKind: z.enum(['fact_find', 'regular']).nullable(),
    date: z.string().nullable(),
    dateLabel: z.string().nullable(),
    durationMin: z.number().nullable(),
    subject: z.string().nullable(),
    sentimentScore: z.number().nullable(),
    sentimentLabel: z.string().nullable(),
    aiSummary: z.string().nullable(),
    complianceNotes: z.string().nullable(),
    topicDistribution: z.array(z.object({ topic: z.string(), pct: z.number() })),
    transcript: z.array(
        z.object({
            id: z.string(),
            timestamp: z.string(),
            speaker: z.string(),
            text: z.string(),
        }),
    ),
    keyInsights: z.array(z.string()),
    opportunityFlags: z.array(z.string()),
    complianceFlags: z.array(z.string()),
});
export type DeskCallRow = z.infer<typeof deskCallRowSchema>;

/** Reviewable lifecycle on the post-call artefact rows (ADR-0002). */
const reviewStatusSchema = z.enum(['pending', 'approved', 'rejected']).nullable();

/** An ATLAS-extracted fact (`call_extracted_facts`). */
export const deskCallFactRowSchema = z.object({
    id: z.string(),
    callId: z.string().nullable(),
    clientId: z.string().nullable(),
    category: z.string().nullable(),
    text: z.string().nullable(),
    confidence: z.string().nullable(),
    sourceQuote: z.string().nullable(),
    accepted: z.boolean().nullable(),
    reviewStatus: reviewStatusSchema,
});
export type DeskCallFactRow = z.infer<typeof deskCallFactRowSchema>;

/** An ATLAS-extracted action item (`call_action_items`). */
export const deskCallActionRowSchema = z.object({
    id: z.string(),
    callId: z.string().nullable(),
    clientId: z.string().nullable(),
    title: z.string().nullable(),
    owner: z.string().nullable(),
    dueDescription: z.string().nullable(),
    note: z.string().nullable(),
    completed: z.boolean().nullable(),
    reviewStatus: reviewStatusSchema,
});
export type DeskCallActionRow = z.infer<typeof deskCallActionRowSchema>;

/** An ATLAS-drafted KB note (`call_kb_notes`). `tags` is a free-form JSON
 *  array on the source row — left loose (genuinely-dynamic vendor payload,
 *  ADR-0018). */
export const deskCallNoteRowSchema = z.object({
    id: z.string(),
    callId: z.string().nullable(),
    clientId: z.string().nullable(),
    title: z.string().nullable(),
    content: z.string().nullable(),
    template: z.string().nullable(),
    tags: z.unknown(),
    reviewStatus: reviewStatusSchema,
});
export type DeskCallNoteRow = z.infer<typeof deskCallNoteRowSchema>;

export const deskCallsMeetingsResponseSchema = z.object({
    calls: z.array(deskCallRowSchema),
    factsByCallId: z.record(z.array(deskCallFactRowSchema)),
    actionsByCallId: z.record(z.array(deskCallActionRowSchema)),
    notesByCallId: z.record(z.array(deskCallNoteRowSchema)),
});
export type DeskCallsMeetingsResponse = z.infer<typeof deskCallsMeetingsResponseSchema>;

// --- Call citation sources --------------------------------------------------
// ATLAS KB-note / AI-summary bodies are markdown that grounds claims with
// inline footnote markers `[^N]`, closed by a `[^N]: <transcript-line-id>`
// definition block (line ids like `bf1836a1-l2`). This endpoint returns the
// call's transcript lines so the UI can resolve each marker to the verbatim
// quote (speaker + timestamp) it cites — the line id alone is meaningless to a
// reader.
export const callCitationSourceSchema = z.object({
    /** Transcript line id, e.g. `bf1836a1-l2` — the `[^N]:` definition target. */
    id: z.string(),
    speaker: z.string(),
    /** Display offset within the call, e.g. `0:13`. */
    timestamp: z.string(),
    text: z.string(),
});
export type CallCitationSource = z.infer<typeof callCitationSourceSchema>;

export const callCitationSourcesResponseSchema = z.object({
    sources: z.array(callCitationSourceSchema),
});
export type CallCitationSourcesResponse = z.infer<typeof callCitationSourcesResponseSchema>;

// --- Ops audit log ----------------------------------------------------------

export const opsAuditLogEntrySchema = z.object({
    id: z.string(),
    at: z.string().optional(),
    kind: z.string(),
    summary: z.string().optional(),
    // Audit-event metadata is heterogeneous per event kind (the action key
    // determines the shape) — a genuinely-dynamic payload (ADR-0018).
    meta: z.record(z.unknown()).optional(),
});
export type OpsAuditLogEntryDto = z.infer<typeof opsAuditLogEntrySchema>;

export const opsAuditLogResponseSchema = z.object({
    entries: z.array(opsAuditLogEntrySchema),
});
export type OpsAuditLogResponse = z.infer<typeof opsAuditLogResponseSchema>;

// --- Aveni calls tile (comply dashboard widget rail) ------------------------
// Projected from the AveniRiskTag (`call_risk_tags`) + `calls` reads.

export const aveniRiskTagRowSchema = z.object({
    id: z.string(),
    callId: z.string(),
    kind: z.string(),
    label: z.string(),
    severity: z.string(),
    offsetMs: z.number(),
    excerpt: z.string(),
});
export type AveniRiskTagRow = z.infer<typeof aveniRiskTagRowSchema>;

export const aveniCallsTileResponseSchema = z.object({
    tags: z.array(aveniRiskTagRowSchema),
    calls: z.array(deskCallRowSchema),
});
export type AveniCallsTileResponse = z.infer<typeof aveniCallsTileResponseSchema>;

// --- Holding-drawer research notes ------------------------------------------
// The `notes` table is a thin (id, clientId) shell; the desk-pages insert path
// writes the richer shape below (it is the only writer). Project to that honest
// DTO so the wire describes what the server sends.

export const holdingNoteRowSchema = z.object({
    id: z.string(),
    isin: z.string().nullable().optional(),
    clientId: z.string().nullable().optional(),
    body: z.string().nullable().optional(),
    authorId: z.string().nullable().optional(),
    createdAt: z.string().nullable().optional(),
});
export type HoldingNoteRow = z.infer<typeof holdingNoteRowSchema>;

// Mandate catalogue (issue #212) — the firm's standard mandate offerings,
// surfaced on /desk/suitability below the review queue. Presentation
// projection of the MandateCatalogueEntry Object Type: `riskGradeLabel` is the
// controller-derived "{min}–{max}" display string so the UI stays dumb.
export const mandateCatalogueEntrySchema = z.object({
    id: z.string(),
    mandateType: z.enum(['Growth', 'Balanced', 'Cautious', 'Income', 'Ethical']),
    name: z.string(),
    riskGradeMin: z.number().int(),
    riskGradeMax: z.number().int(),
    riskGradeLabel: z.string(),
    targetReturnLabel: z.string(),
    benchmark: z.string(),
    displayOrder: z.number().int().nullable(),
    notes: z.string().nullable(),
});
export type MandateCatalogueEntryDto = z.infer<typeof mandateCatalogueEntrySchema>;

export const mandateCatalogueResponseSchema = z.object({
    entries: z.array(mandateCatalogueEntrySchema),
});
export type MandateCatalogueResponse = z.infer<typeof mandateCatalogueResponseSchema>;

// Portfolio holdings — the IM's book, sourced from the ValuationGrid
// (`client_holdings`) scoped to the clients they manage and aggregated by ISIN
// in the controller. The grid carries the custodian valuation as at its value
// date, so unlike the value-less custodian recon feeds this projection DOES
// carry `valuationGbp` (real, not fabricated — ADR-0005). `portfolioCount` is
// how many of the IM's portfolios hold the ISIN. The OpenFIGI monogram resolves
// from `isin` + `securityName`.
export const deskPortfolioHoldingSchema = z.object({
    isin: z.string(),
    securityName: z.string(),
    // Sum of quantity across every portfolio of the IM's clients holding this
    // ISIN. Unit-agnostic (shares / nominal).
    quantity: z.number(),
    // Sum of the grid valuation (£) across those holdings.
    valuationGbp: z.number(),
    // Number of distinct portfolios (client accounts) holding this ISIN.
    portfolioCount: z.number(),
});
export type DeskPortfolioHoldingDto = z.infer<typeof deskPortfolioHoldingSchema>;

export const deskPortfolioHoldingsResponseSchema = z.object({
    holdings: z.array(deskPortfolioHoldingSchema),
});
export type DeskPortfolioHoldingsResponse = z.infer<typeof deskPortfolioHoldingsResponseSchema>;

// --- FeedItem lifecycle (Ops Command Centre) --------------------------------
// The three lifecycle mutations dispatch through the FeedItem Object Type's
// Actions (ADR-0008) and echo the updated row. The row is a real DTO (the
// server reads it as a typed FeedItem) — not the generic action-result `data`
// envelope.

export const feedItemDtoSchema = z.object({
    id: z.string(),
    timestamp: z.string().nullable(),
    category: z.string().nullable(),
    severity: z.string().nullable(),
    title: z.string().nullable(),
    detail: z.string().nullable(),
    aiAssessment: z.string().nullable(),
    proposedAction: z.string().nullable(),
    status: z.enum(['New', 'In Progress', 'Resolved']),
});
export type FeedItemDto = z.infer<typeof feedItemDtoSchema>;

/** Staff-action OK envelope specialised to carry the updated FeedItem row. */
export const feedItemActionOkSchema = z.object({
    ok: z.literal(true),
    data: z.object({ feedItem: feedItemDtoSchema }),
    invalidate: z.array(z.string()).optional(),
});
export type FeedItemActionOk = z.infer<typeof feedItemActionOkSchema>;

export const deskPagesContract = c.router(
    {
        getClientKnowledge: {
            method: 'GET',
            path: '/desk/client-knowledge',
            responses: { 200: deskClientKnowledgeResponseSchema, 403: errorBody },
            summary: 'Desk > Client Knowledge reference data',
        },
        // Attach an IM-authored Markdown document to a client's knowledge base.
        // Dispatches the AddClientKnowledgeDocument Action (ADR-0008) and echoes
        // the created document as a DTO.
        addClientKnowledgeDocument: {
            method: 'POST',
            path: '/desk/client-knowledge/documents',
            body: z.object({
                clientId: z.string().min(1),
                title: z.string().min(1),
                markdownContent: z.string().min(1),
                sourceFilename: z.string().optional(),
            }),
            responses: {
                200: z.object({ document: clientKnowledgeDocumentSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Add an IM-authored Markdown document to a client knowledge base',
        },
        // Soft-delete an IM-authored Markdown document (only the manual ones —
        // ATLAS artefacts are immutable here). Dispatches the
        // DeleteClientKnowledgeDocument Action (ADR-0008).
        deleteClientKnowledgeDocument: {
            method: 'POST',
            path: '/desk/client-knowledge/documents/:id/delete',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: z.object({ ok: z.literal(true), id: z.string() }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Soft-delete an IM-authored client knowledge document',
        },
        getCallsMeetings: {
            method: 'GET',
            path: '/desk/calls-meetings',
            responses: { 200: deskCallsMeetingsResponseSchema, 403: errorBody },
            summary: 'Desk > Calls & Meetings composed payload',
        },
        getCallCitationSources: {
            method: 'GET',
            path: '/desk/calls/:callId/citation-sources',
            pathParams: z.object({ callId: z.string() }),
            responses: { 200: callCitationSourcesResponseSchema, 403: errorBody },
            summary: "Transcript-line sources for resolving a call summary's [^N] citations",
        },
        getMandateCatalogue: {
            method: 'GET',
            path: '/desk/suitability/mandate-catalogue',
            responses: { 200: mandateCatalogueResponseSchema, 403: errorBody },
            summary: 'Desk > Suitability mandate catalogue (firm offerings)',
        },
        getPortfolioHoldings: {
            method: 'GET',
            path: '/desk/portfolio/holdings',
            responses: { 200: deskPortfolioHoldingsResponseSchema, 403: errorBody },
            summary: 'Desk > Portfolio aggregate holdings (firm book, value-less)',
        },
        getOpsAuditLog: {
            method: 'GET',
            path: '/ops/audit-log',
            responses: { 200: opsAuditLogResponseSchema, 403: errorBody },
            summary: 'Ops audit log entries (admin / view persona)',
        },
        getAveniCallsTile: {
            method: 'GET',
            path: '/widgets/aveni-calls',
            responses: { 200: aveniCallsTileResponseSchema, 403: errorBody },
            summary: 'Aveni risk-tag tile data (call_risk_tags + calls)',
        },
        listHoldingNotes: {
            method: 'GET',
            path: '/desk/holdings/:isin/notes',
            pathParams: z.object({ isin: z.string() }),
            responses: {
                200: z.object({ notes: z.array(holdingNoteRowSchema) }),
                403: errorBody,
            },
            summary: 'Per-ISIN research notes (replaces useTable on `notes`)',
        },
        insertHoldingNote: {
            method: 'POST',
            path: '/desk/holdings/:isin/notes',
            pathParams: z.object({ isin: z.string() }),
            body: z.object({
                clientId: z.string().optional(),
                body: z.string(),
                authorId: z.string().optional(),
            }),
            responses: {
                200: z.object({ note: holdingNoteRowSchema }),
                400: errorBody,
                403: errorBody,
            },
            summary: 'Append a research note to a holding',
        },
        insertSuitabilityAlert: {
            method: 'POST',
            path: '/desk/holdings/:isin/suitability-alerts',
            pathParams: z.object({ isin: z.string() }),
            // The holding-drawer raise dispatches the RaiseSuitabilityAlert
            // Action; the body carries the drawer's display vocabulary, mapped
            // onto the Action input server-side. Loose because the drawer
            // collects free-form display fields (a thin client-side form, not a
            // structured domain payload) — the Action's own input is the typed
            // contract for what actually lands.
            body: z.record(z.unknown()),
            responses: {
                // `alert` is the generic action-result `data` envelope from the
                // Action invocation (named-loose per ADR-0018).
                200: z.object({ alert: z.record(z.unknown()) }),
                400: errorBody,
                403: errorBody,
            },
            summary: 'Insert a suitability alert raised from the holding drawer',
        },

        // -----------------------------------------------------------------
        // Feed-items lifecycle (OLD_CODE.html #2 replacement for the
        // desk-resolve-action / desk-snooze-action / desk-escalate-action
        // named handlers). Each dispatches through the FeedItem Object Type's
        // lifecycle Action (ADR-0008) and echoes the updated row.
        // -----------------------------------------------------------------
        resolveFeedItem: {
            method: 'POST',
            path: '/desk/feed-items/:id/resolve',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: feedItemActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Mark a feed-item Resolved.',
        },
        snoozeFeedItem: {
            method: 'POST',
            path: '/desk/feed-items/:id/snooze',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ days: z.number().int().positive().optional() }).optional(),
            responses: {
                200: feedItemActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Snooze a feed-item (default 7 days).',
        },
        escalateFeedItem: {
            method: 'POST',
            path: '/desk/feed-items/:id/escalate',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: feedItemActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Escalate a feed-item to compliance.',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
